// The wizard step segments, shared by the server (path validation, redirects) and the client
// (the language picker validates the step it is asked to return to). It lives in universal because
// both layers must agree on the same list and universal code cannot import from src/server.
export const ALLOWED_PATHS_LIST = {
    default: '',
    propertyDetails: 'property-details',
    propertyInfo: 'property-info',
    classification: 'classification',
    languageSelection: 'languageSelection',
    location: 'location',
    manualLocation: 'manual-location',
    confirmLocation: 'confirm-location',
    lead: 'lead',
    eligibilityVrbo: 'eligibility/vrbo',
    eligibilitySize: 'eligibility/size',
    eligibilityLocation: 'eligibility/location',
    eligibilityAddProperty: 'eligibility/add-property',
    joinConfirmation: 'join-confirmation',
    emailVerification: 'email-verification',
    updateAccount: 'update-account',
    loginInterstitial: 'login-interstitial',
    missingDetails: 'missing-details',
    estimateResults: 'estimate-results',
    addPropertyPropertyInfo: 'add-property/property-info',
    addPropertyManualLocation: 'add-property/manual-location',
    addPropertyConfirmLocation: 'add-property/confirm-location',
    addPropertyLocation: 'add-property/location',
    addPropertyJoinTimeout: 'add-property/join/timeout',
    addPropertyIneligible: 'add-property/ineligible',
    addPropertyJoinCreation: 'add-property/join-creation',
    joinCreation: 'join-creation',
    joinCreationConfirm: 'join-creation/confirm',
    agenticJoinCreation: 'agentic/join-creation',
    propertyCreation: 'property-creation',
    join: 'join'
};

export const ALLOWED_PATHS = new Set(Object.values(ALLOWED_PATHS_LIST));

// The step a visitor originally asked for, carried across the language-selection detour so the
// picker can return them to it in the locale they choose. Written by redirectOnLocale (server) and
// read by the LanguageSelection component (client) — it lives here so those two cannot drift on
// the spelling.
export const RETURN_TO_STEP_PARAM = 'returnToStep';

// Where the picker goes when it has no step to return to, e.g. the visitor landed on the bare
// /{locale}/list entry route, or reached the picker from inside the flow via PageConfig. This is
// the destination the picker used unconditionally before OBS-3056.
export const DEFAULT_RETURN_STEP = ALLOWED_PATHS_LIST.propertyDetails;

// The picker interpolates this value into a URL it navigates to, so it must be whitelisted rather
// than sanitized. Rejects unknown steps, traversal (`../`), absolute and protocol-relative URLs,
// and languageSelection itself (returning to the picker would loop).
export const safeReturnToStep = (requested) => {
    if (!requested || typeof requested !== 'string') return DEFAULT_RETURN_STEP;
    if (requested === ALLOWED_PATHS_LIST.languageSelection) return DEFAULT_RETURN_STEP;
    return ALLOWED_PATHS.has(requested) && requested !== ALLOWED_PATHS_LIST.default
        ? requested
        : DEFAULT_RETURN_STEP;
};

// The step whose experience this request should render. A visitor detoured to the language picker
// on their way to a step should already get that step's treatment, rather than seeing one
// experience and then a different one the moment they pick a locale.
export const resolveEffectiveStep = (step, query = {}) => (
    step === ALLOWED_PATHS_LIST.languageSelection
        ? safeReturnToStep(query[RETURN_TO_STEP_PARAM])
        : step
);

export default {
    ALLOWED_PATHS_LIST,
    ALLOWED_PATHS,
    RETURN_TO_STEP_PARAM,
    DEFAULT_RETURN_STEP,
    safeReturnToStep,
    resolveEffectiveStep
};
